import {
  FC,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import ListAPIContext from "../contexts/ListAPIContext";
import ListAPIContextProvider from "../contexts/ListAPIContext/Provider";
import { Loading, Theme } from "@analyticsMD/quil";

import { PrintOutlined } from "@mui/icons-material";
import RoundingSession from "../types/RoundingSession";

import Department from "../types/Department";
import DepartmentGroup from "../types/DepartmentGroup";

import { Box, Button } from "@mui/material";

import { deepPurple, grey } from "@mui/material/colors";
import {
  GridColumnVisibilityModel,
  GridFilterModel,
  GridSortModel,
  gridFilteredSortedRowEntriesSelector,
  useGridApiRef,
} from "@mui/x-data-grid";
import { ErrorBoundary } from "react-error-boundary";
import ErrorPage from "../components/error-page";
import {
  usePatientListFiltersQuery,
  usePatientListQuery,
} from "../api/queries";
import EmbededAppContainer from "../components/embedded-container";
import { Sizes } from "../types/loadingSizes";
import NotificationToast from "../components/notification-toast";
import RoundingButtonStandalone from "../components/rounding/rounding-button-standalone";
import SimpleNotificationToast from "../components/simple-notification-toast";
import Disposition from "../types/Disposition";

import DepartmentSelector from "../components/list/department-selector";
import ListPageShell from "../components/list/list-page-shell";
import { useFilteredRows } from "../components/list/use-filtered-rows";
import { useListDrawer } from "../components/list/use-list-drawer";
import {
  buildViewOptions,
  findViewOption,
  viewQueryParams,
} from "../components/list/view-options";
import PlaceholderSection from "../components/sections/placeholder-section";
import PatientDrawer from "../components/list/patient-drawer";
import PatientListEntry from "../types/PatientListEntry";
import FilterToolbar from "../components/filter/filter-toolbar";
import Configuration from "../types/Configuration";

import { useMutation, useQueryClient } from "react-query";
import ListFilter from "../types/ListFilter";
import { getPatientListColumns } from "../columns/patientlist.columns";
import type { CareTeamSortField } from "../components/list/care-team-column-header";
import { patchUserPreferences } from "../api/endpoints";

import moment from "moment";
import {
  patchRoundingPatientEntry,
  postRoundingPatientEntry,
} from "../api/endpoints";
import { printPatientList } from "../utils/print/print-patient-list";
import { usePatientListDetails } from "../hooks/use-patient-list-details";
import { useUpdatePatientCache } from "../hooks/use-update-patient-cache";
import ProductionBanner from "../components/production-banner";
import { EDDWindows } from "../types/EDDWindows";

const ROW_HEIGHT = 116;

const sxStyles = {
  printButton: {
    border: "none",
    textTransform: "none",
    color: deepPurple[800],
    "&:hover": {
      backgroundColor: grey["A100"],
    },
  },
  printWrapper: {
    position: "absolute",
    right: 0,
    paddingRight: "0.75rem",
  },
};

const TOAST_ERROR_MESSAGE =
  "We encountered an issue while updating patient data. Please try again";

const TOAST_ROUNDING_MESSAGE =
  "Rounds completed. Your changes have been saved.";

const Layout: FC = () => {
  const {
    configurationQuery,
    departmentsQuery,
    departmentGroupsQuery,
    activeRoundingSessionQuery,
    dispositionsQuery,
    userPreferencesQuery,
    productionDataReadOnly,
    analytics,
    showErrorToast,
    setShowErrorToast,
  } = useContext(ListAPIContext);
  const allQueries = [
    configurationQuery,
    departmentsQuery,
    departmentGroupsQuery,
    activeRoundingSessionQuery,
    userPreferencesQuery,
  ];

  const allQueriesSuccessful = allQueries.every((query) => query.isSuccess);
  const anyQueryErrors = allQueries.some((query) => query.isError);
  const queryErrors = () => {
    return new Error(
      allQueries
        .filter((query) => query.isError)
        .map((query) => query.error)
        .join(", ")
    );
  };

  const configuration: Configuration = useMemo(
    () => (configurationQuery?.data ? configurationQuery?.data?.[0] : {}),
    [configurationQuery]
  );

  const activeRoundingSession: RoundingSession | null =
    (allQueriesSuccessful && activeRoundingSessionQuery?.data) || null;

  const departments: Department[] =
    (allQueriesSuccessful && departmentsQuery?.data) || [];
  const departmentGroups: DepartmentGroup[] =
    (allQueriesSuccessful && departmentGroupsQuery?.data) || [];

  const selectableDispositions = useMemo(
    () => (dispositionsQuery.data as Disposition[]) || [],
    [dispositionsQuery]
  );

  const userPreferences =
    (allQueriesSuccessful && userPreferencesQuery?.data?.[0]) || {};

  const departmentOptions = buildViewOptions(departments, departmentGroups);

  // The stored board, which the effect below selects once its option loads.
  const preferredOption = departmentOptions.find(
    (d) =>
      d.id === userPreferences.default_department ||
      d.label === userPreferences.default_department
  );

  const [selectedDept, setSelectedDept] = useState({
    id: null,
    label: null,
    hospitalID: null,
    isHospitalWide: null,
    eddWindow: null,
  });
  const {
    selectedRow: selectedPatient,
    isOpen: drawerOpen,
    open: openDrawer,
    close: closeDrawer,
    rowSelectionModel,
    setRowSelectionModel,
  } = useListDrawer<PatientListEntry>();
  const [showRoundingToast, setShowRoundingToast] = useState(false);

  const listParams = viewQueryParams(selectedDept);

  const patientListQuery = usePatientListQuery(
    listParams.department,
    listParams.group,
    listParams.hospitalID
  );

  const patientListBaseData = useMemo(
    () => patientListQuery.data || [],
    [patientListQuery.data]
  );

  const { data: patientListData, isLoading: detailsLoading } =
    usePatientListDetails(patientListBaseData);

  const dataGridRef = useGridApiRef();

  useEffect(() => {
    // if they have a stored dept, use it if it is an available option
    if (preferredOption && !selectedDept.id) {
      setSelectedDept(preferredOption);
    }
  }, [preferredOption, selectedDept.id]);

  // The board on screen; until the effect selects the stored one, show that.
  const pickerValue = selectedDept.id
    ? findViewOption(departmentOptions, selectedDept)
    : preferredOption ?? null;

  // Track active filters in parent component
  const [activeFilters, setActiveFilters] = useState<ListFilter[]>([]);

  const patientListFiltersQuery = usePatientListFiltersQuery(
    listParams.department,
    listParams.group,
    listParams.hospitalID
  );

  const listQueryKey = useMemo(
    () => ["patientlist", listParams.department, listParams.group],
    [listParams.department, listParams.group]
  );

  const filterQueryKey = useMemo(
    () => ["patientlistfilters", listParams.department, listParams.group],
    [listParams.department, listParams.group]
  );

  const patientListFilters = patientListFiltersQuery.data || [];
  const [filterModel, setFilterModel] = useState<GridFilterModel>(null);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "location", sort: "asc" }, // default sort is by location
  ]);
  const [careTeamSortBy, setCareTeamSortBy] =
    useState<CareTeamSortField>("attending_provider");

  /** When the View (dept / group) changes, clear Care Team sort and sub-sort choice */
  const prevPatientListViewKeyRef = useRef<string | number | null>(null);
  useEffect(() => {
    const viewKey = selectedDept.id ?? selectedDept.label;
    if (viewKey == null || viewKey === "") {
      return;
    }
    const prevKey = prevPatientListViewKeyRef.current;
    if (prevKey !== null && prevKey !== viewKey) {
      setCareTeamSortBy("attending_provider");
      const api = dataGridRef.current;
      const withoutCareTeam = (model: GridSortModel) =>
        model.filter((s) => s.field !== "care_team");
      if (api) {
        const current = api.getSortModel();
        const next = withoutCareTeam(current);
        api.setSortModel(
          next.length > 0 ? next : [{ field: "location", sort: "asc" }]
        );
      } else {
        setSortModel((prevModel) => {
          const next = withoutCareTeam(prevModel);
          return next.length > 0 ? next : [{ field: "location", sort: "asc" }];
        });
      }
    }
    prevPatientListViewKeyRef.current = viewKey;
  }, [selectedDept.id, selectedDept.label, dataGridRef]);

  const queryClient = useQueryClient();

  const selectedPatientRef = useRef(null);

  const columns = useMemo(
    () =>
      getPatientListColumns(
        activeRoundingSession,
        selectedDept,
        selectableDispositions,
        configuration,
        setShowErrorToast,
        analytics,
        {
          sortBy: careTeamSortBy,
          setSortBy: setCareTeamSortBy,
        },
        selectedPatientRef,
        productionDataReadOnly
      ),
    [
      analytics,
      careTeamSortBy,
      configuration,
      selectedDept,
      selectableDispositions,
      activeRoundingSession,
      productionDataReadOnly,
      setShowErrorToast,
    ]
  );

  const printList = () => {
    const api = dataGridRef.current;
    const rowsForPrint =
      api != null
        ? gridFilteredSortedRowEntriesSelector(dataGridRef).map(
            (entry) => entry.model as PatientListEntry
          )
        : filteredPatientListData;

    printPatientList(rowsForPrint, {
      departmentName: selectedDept.label,
      configuration,
    });
  };

  const filteredPatientListData = useFilteredRows(
    patientListData,
    columns,
    activeFilters
  );

  const getRowHeight = useCallback(() => ROW_HEIGHT, []);

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({
      rounding_status: false,
      rounding_status_placeholder: true,
    });

  useEffect(() => {
    setColumnVisibilityModel({
      rounding_status: activeRoundingSession ? true : false,
      rounding_status_placeholder: activeRoundingSession ? false : true,
    });
  }, [activeRoundingSession]);

  const patchRoundingPatientEntryMutation = useMutation(
    patchRoundingPatientEntry,
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: ["roundingsession"] });
      },
      onError: () => {
        setShowErrorToast(true);
      },
    }
  );

  const postRoundingPatientEntryMutation = useMutation(
    postRoundingPatientEntry,
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: ["roundingsession"] });
      },
      onError: () => {
        setShowErrorToast(true);
      },
    }
  );

  const createPatientEntry = useCallback(
    (patient) => {
      postRoundingPatientEntryMutation.mutate({
        patient: patient.id,
        is_rounded: true,
        rounded_timestamp: moment().toISOString(true),
        rounding_session: activeRoundingSession.id,
      });
    },
    [postRoundingPatientEntryMutation, activeRoundingSession?.id]
  );

  const { invalidateAllListQueries } = useUpdatePatientCache();

  const invalidateListQueries = useCallback(() => {
    queryClient.invalidateQueries(listQueryKey);
    queryClient.invalidateQueries(filterQueryKey);

    // Invalidate detail list queries so drawer changes are reflected
    invalidateAllListQueries();

    // also clear the rounding session query
    if (activeRoundingSession) {
      queryClient.invalidateQueries("roundingsession");
    }
  }, [
    queryClient,
    listQueryKey,
    filterQueryKey,
    invalidateAllListQueries,
    activeRoundingSession,
  ]);

  const handleCellClick = useCallback(
    (params) => {
      // if the drawer is open and we're switching patients, we should update the list
      if (drawerOpen && selectedPatient?.id !== params.row.id) {
        invalidateListQueries();
      }

      if (
        ![
          "current_edd",
          "current_dispositions",
          "medically_ready_status",
        ].includes(params.field)
      ) {
        openDrawer(params.row);

        // store the current selected patient to let us know if we should disable fields
        // when the drawer is open
        if (selectedPatientRef) {
          selectedPatientRef.current = params.row.id;
        }
      } else if (activeRoundingSession && !productionDataReadOnly) {
        // handle updating rounding entries for patients upon interaction
        const entry = activeRoundingSession.patient_entries.find(
          (p) => p.patient === params.row.id
        );

        if (entry) {
          const payload = {
            id: entry.id,
            is_rounded: true,
            rounded_timestamp: moment().toISOString(true),
            rounding_session: activeRoundingSession.id,
          };

          patchRoundingPatientEntryMutation.mutate(payload);
        } else {
          createPatientEntry(params.row);
        }
      }
    },
    [
      drawerOpen,
      selectedPatient?.id,
      openDrawer,
      activeRoundingSession,
      invalidateListQueries,
      patchRoundingPatientEntryMutation,
      createPatientEntry,
      productionDataReadOnly,
    ]
  );

  const handleRowClick = useCallback((params, event) => {
    const cell = event.target.closest?.(".MuiDataGrid-cell");
    const field = cell?.getAttribute("data-field");
    if (
      field &&
      [
        "current_edd",
        "current_dispositions",
        "medically_ready_status",
      ].includes(field)
    ) {
      event.defaultMuiPrevented = true;
    }
  }, []);

  // for starting rounds, we want to open QCard for the first patient in the list if we don't already have a drawer open
  const handleInitialDrawerOpen = () => {
    if (!selectedPatient && !drawerOpen && filteredPatientListData.length > 0) {
      const sortedRows = dataGridRef.current
        ? (dataGridRef.current.getSortedRows() as PatientListEntry[])
        : filteredPatientListData;

      openDrawer(sortedRows[0]);
    }
  };

  const handleDrawerClose = () => {
    closeDrawer();

    if (selectedPatientRef) {
      selectedPatientRef.current = null;
    }

    invalidateListQueries();
  };

  // Simplified handleSetFilter:
  const handleSetFilter = (newFilter, currentFilters) => {
    // Update active filters state - useMemo will automatically recompute filteredPatientListData
    setActiveFilters(currentFilters || []);

    if (!newFilter) {
      return;
    }

    // empty filter value means remove this filter
    if (newFilter.value === "") {
      const remainingFilters = currentFilters.filter(
        (f) => f.id !== newFilter.id && Object.hasOwn(f, "value")
      );
      setActiveFilters(remainingFilters);
      return;
    }

    // apply all filters with values
    const validFilters = currentFilters.filter((f) =>
      Object.hasOwn(f, "value")
    );
    setActiveFilters(validFilters);
  };

  const patchUserPreferencesMutation = useMutation(patchUserPreferences, {
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["userpreferences"] });
    },
  });

  const handleUpdateDefaultDepartmentPreference = (defaultDepartment) => {
    const newPreferences = {
      ...userPreferences,
      default_department: defaultDepartment.id,
    };
    patchUserPreferencesMutation.mutate(newPreferences);
  };

  const eddFilter = patientListFilters.find((f) => f.field === "edd");

  useEffect(() => {
    if (!selectedDept.id) return;

    const shouldSetDefault = !!(
      selectedDept.isHospitalWide && selectedDept.eddWindow
    );

    const eddWindow =
      selectedDept.eddWindow === EDDWindows.ALL
        ? [EDDWindows.TODAY, EDDWindows.TOMORROW, EDDWindows.DAY_AFTER_TOMORROW]
        : [selectedDept.eddWindow];

    const defaultEddFilter =
      eddFilter && shouldSetDefault ? { ...eddFilter, value: eddWindow } : null;

    if (defaultEddFilter) {
      setActiveFilters([defaultEddFilter]);
    } else {
      setActiveFilters([]);
    }
  }, [
    selectedDept.id,
    selectedDept.isHospitalWide,
    selectedDept.eddWindow,
    eddFilter,
  ]);

  if (!allQueriesSuccessful && !anyQueryErrors) {
    return (
      <Box sx={{ marginTop: "25%" }} data-testid="page">
        <Loading message={"Loading..."} logoSize={Sizes.Medium} />
      </Box>
    );
  }

  if (!configuration.patient_list_enabled) {
    return (
      <PlaceholderSection
        placeholderText="This feature is not currently available."
        isMaintenance
      />
    );
  }

  return (
    <Theme>
      <Box>
        <EmbededAppContainer>
          {allQueriesSuccessful && !anyQueryErrors && (
            <ListPageShell
              banner={
                <ProductionBanner username={userPreferences.emr_user_id} />
              }
              viewSelector={
                <DepartmentSelector
                  options={departmentOptions}
                  value={pickerValue}
                  setSelectedDept={setSelectedDept}
                  handleUpdateDefaultDepartment={
                    handleUpdateDefaultDepartmentPreference
                  }
                />
              }
              headerActions={
                <>
                  {configuration.rounding_mode_enabled && (
                    <RoundingButtonStandalone
                      activeSession={activeRoundingSession}
                      setShowRoundingToast={setShowRoundingToast}
                      setShowErrorToast={setShowErrorToast}
                      handleDrawerOpen={handleInitialDrawerOpen}
                      handleDrawerClose={handleDrawerClose}
                      queryKey={filterQueryKey}
                      analytics={analytics}
                      readOnly={productionDataReadOnly}
                    />
                  )}
                  <Box sx={sxStyles.printWrapper}>
                    <Button
                      variant="outlined"
                      startIcon={<PrintOutlined />}
                      sx={sxStyles.printButton}
                      onClick={printList}
                    >
                      Print
                    </Button>
                  </Box>
                </>
              }
              filters={
                patientListData.length > 0 && (
                  <FilterToolbar
                    key={selectedDept.id || "default"}
                    filterOptions={patientListFilters}
                    activeFilters={activeFilters}
                    handleSetFilter={handleSetFilter}
                  />
                )
              }
              gridProps={{
                apiRef: dataGridRef,
                loading: patientListQuery.isLoading || detailsLoading,
                rows: filteredPatientListData,
                columns,
                getRowHeight,
                columnVisibilityModel,
                onColumnVisibilityModelChange: (newModel) =>
                  setColumnVisibilityModel(newModel),
                onCellClick: handleCellClick,
                onRowClick: handleRowClick,
                // restore the quick search and/or sort order
                initialState: {
                  filter: {
                    filterModel: filterModel ? filterModel : null,
                  },
                  sorting: {
                    sortModel: sortModel,
                  },
                },
                // persist the current sort order
                onSortModelChange: (newSortModel) => setSortModel(newSortModel),
                rowSelectionModel,
                // persist the currently selected row
                onRowSelectionModelChange: (newRowSelectionModel) =>
                  setRowSelectionModel(newRowSelectionModel),
                // persist the quick search filter
                onFilterModelChange: (newFilterModel) =>
                  setFilterModel(newFilterModel),
              }}
              drawer={{
                isOpen: drawerOpen,
                rowId: selectedPatient?.id,
                onClose: handleDrawerClose,
                renderContent: (closeButton) => (
                  <PatientDrawer
                    patient={selectedPatient}
                    updateRef={null}
                    closeButtonCmpt={closeButton}
                  />
                ),
              }}
            >
              <NotificationToast
                message={TOAST_ERROR_MESSAGE}
                showToast={showErrorToast}
                isError={true}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                includeTopOffset={false}
                onClose={() => setShowErrorToast(false)}
              />
              <SimpleNotificationToast
                message={TOAST_ROUNDING_MESSAGE}
                showToast={showRoundingToast}
                autoHideDuration={2000}
                onClose={() => setShowRoundingToast(false)}
              />
            </ListPageShell>
          )}
          {anyQueryErrors && <ErrorPage error={queryErrors()} />}
        </EmbededAppContainer>
      </Box>
    </Theme>
  );
};

const PatientListPage: FC = () => {
  return (
    <ListAPIContextProvider>
      <ErrorBoundary FallbackComponent={ErrorPage}>
        <Layout />
      </ErrorBoundary>
    </ListAPIContextProvider>
  );
};

export default PatientListPage;
